import { colorPalette, freeColorIds } from "../../design/productConfig";
import {
  FREE_VEHICLE_PRESETS,
  HORN_IDS,
  VEHICLE_PRESETS,
  type DriverProfile,
  type HornId,
  type VehiclePreset,
} from "../../types/traffic";

const STORAGE_KEY = "internet-traffic-jam:mock-driver:v1";
function randomItem<T>(items: readonly T[]): T {
  const index = Math.floor(Math.random() * items.length);
  const item = items[index];
  if (item === undefined) throw new Error("Cannot select from an empty list.");
  return item;
}

function createSystemPlate(): string {
  const number = Math.floor(100 + Math.random() * 900);
  return `JAM-${number}`;
}

function createPublicId(): string {
  return `drv_${crypto.randomUUID().replaceAll("-", "")}`;
}

export function createMockDriver(): DriverProfile {
  return {
    publicDriverId: createPublicId(),
    plate: createSystemPlate(),
    vehiclePreset: randomItem(FREE_VEHICLE_PRESETS),
    colorId: randomItem(freeColorIds),
    hornId: "classic",
    badge: null,
    garageOwned: false,
    odometerMiles: 0,
  };
}

function isVehiclePreset(value: unknown): value is VehiclePreset {
  return (
    typeof value === "string" &&
    VEHICLE_PRESETS.includes(value as VehiclePreset)
  );
}

function isHornId(value: unknown): value is HornId {
  return typeof value === "string" && HORN_IDS.includes(value as HornId);
}

function parseStoredDriver(value: unknown): DriverProfile | null {
  if (typeof value !== "object" || value === null) return null;
  const candidate = value as Partial<DriverProfile>;

  const valid =
    typeof candidate.publicDriverId === "string" &&
    (/^JAM-\d{3}$/.test(candidate.plate ?? "") ||
      /^[A-Z0-9]+(?: [A-Z0-9]+)?$/.test(candidate.plate ?? "")) &&
    isVehiclePreset(candidate.vehiclePreset) &&
    typeof candidate.colorId === "string" &&
    candidate.colorId in colorPalette &&
    isHornId(candidate.hornId) &&
    (candidate.badge === null ||
      candidate.badge === "garage" ||
      candidate.badge === "founding") &&
    typeof candidate.odometerMiles === "number" &&
    candidate.odometerMiles >= 0;
  if (!valid) return null;
  return {
    ...candidate,
    garageOwned: candidate.garageOwned === true,
  } as DriverProfile;
}

export function loadMockDriver(): DriverProfile | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return parseStoredDriver(parsed);
  } catch {
    return null;
  }
}

export function saveMockDriver(driver: DriverProfile): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(driver));
}
